/**
 * GuestWelcomeCard · iter149
 *
 * Ersetzt den alten breiten grauen `auth-banner` in der Feed-Mitte für ausgeloggte
 * Besucher. Zweigeteilt: links Markenbotschaft, rechts CTAs. Kompakt, hochwertig,
 * NICHT vollbreite. Passt sich per Grid an; Bild-Slot als Gradient-Fallback.
 *
 * Fußnote: Hinweis dass MP + STX auch ohne Konto erkundbar sind.
 */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ShoppingBag, Home, X, LogIn } from "lucide-react";
import Brand from "@/components/common/Brand";

const DISMISS_KEY = "stx.guest_welcome_dismissed_v1";

export default function GuestWelcomeCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  if (dismissed) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      data-testid="guest-welcome-card"
      className="mx-3 mt-3 rounded-2xl overflow-hidden border border-[#E5E5E5] bg-gradient-to-br from-[#0A0A0A] via-[#1a1a1a] to-[#333] text-white relative"
    >
      {/* iter151 · X-Button rechts oben zum dauerhaften Ausblenden */}
      <button
        type="button"
        data-testid="guest-welcome-dismiss"
        onClick={dismiss}
        aria-label="Karte ausblenden"
        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition"
      >
        <X size={16} />
      </button>
      <div className="p-6 md:p-8">
        <p className="text-xs text-white/70 mb-2 flex items-center gap-1.5">
          <Sparkles size={12} /> <Brand />
        </p>
        <h2 className="text-2xl md:text-3xl font-black leading-tight max-w-xl">
          Entdecke <Brand />
        </h2>
        <p className="text-sm md:text-base text-white/80 mt-2 max-w-2xl">
          {t("guest_welcome.subtitle", {
            defaultValue: "Marketplace, Creator und exklusive Inhalte an einem Ort.",
          })}
        </p>
        <div className="flex gap-4 mt-4 text-[11px] text-white/60">
          <span className="flex items-center gap-1"><Home size={12} /> Social</span>
          <span className="flex items-center gap-1"><ShoppingBag size={12} /> Marketplace</span>
          <span className="flex items-center gap-1"><Sparkles size={12} /> <Brand suffix="X" /></span>
        </div>
        {/* iter161 · Mobile Login-CTA: auf Handy sichtbar (kein Sidebar-Login-Formular),
            auf Desktop ausgeblendet (rechts existiert bereits das Login-Formular). */}
        <div className="mt-5 flex gap-2 xl:hidden">
          <button
            type="button"
            data-testid="guest-welcome-login-btn"
            onClick={() => navigate("/login")}
            className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-white text-[#0A0A0A] font-semibold text-sm hover:bg-white/90 active:scale-[0.98] transition"
          >
            <LogIn size={14} /> {t("auth.login", { defaultValue: "Einloggen" })}
          </button>
          <button
            type="button"
            data-testid="guest-welcome-register-btn"
            onClick={() => navigate("/register")}
            className="inline-flex items-center justify-center h-11 px-5 rounded-full border border-white/40 text-white font-medium text-sm hover:bg-white/10 active:scale-[0.98] transition"
          >
            {t("auth.register", { defaultValue: "Registrieren" })}
          </button>
        </div>
        {/* iter154 · „Anmelden rechts →" entfernt weil im Handy-Format nichts rechts sichtbar ist */}
        <p className="text-[10px] text-white/50 mt-4">
          Du kannst Marketplace und <Brand suffix="X" /> auch ohne Konto entdecken.
        </p>
      </div>
    </div>
  );
}
